import { useState, useCallback, useEffect, useRef } from "react";
import { api } from "../services/api";
import { ApiError } from "../services/api/BaseApi";

/** Validation constants matching backend rules */
const DISPLAY_NAME_MIN = 3;
const DISPLAY_NAME_MAX = 30;
const DISPLAY_NAME_PATTERN = /^[a-zA-Z0-9_-]+$/;
const BIO_MAX = 300;
const AVATAR_ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const AVATAR_MAX_SIZE = 5 * 1024 * 1024; // 5 MB
const SUBMIT_TIMEOUT_MS = 30000;

export interface EditProfileFormErrors {
  displayName?: string;
  bio?: string;
  avatar?: string;
  general?: string;
}

export interface EditProfileUserData {
  username: string;
  bio: string;
  avatarUrl?: string;
}

export interface UseEditProfileFormResult {
  displayName: string;
  bio: string;
  avatarFile: File | null;
  avatarPreview: string | null;
  selectedAvatarId: string | null;
  errors: EditProfileFormErrors;
  isSubmitting: boolean;
  bioRemaining: number;
  handleDisplayNameChange: (value: string) => void;
  handleBioChange: (value: string) => void;
  handleAvatarSelect: (file: File) => void;
  handlePresetAvatarSelect: (avatarId: string, previewUrl: string) => void;
  handleSubmit: () => Promise<boolean>;
}

/**
 * Validates a display name against backend rules.
 * Returns an error message or undefined if valid.
 */
export function validateDisplayName(value: string): string | undefined {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return "Display name is required";
  }
  if (trimmed.length < DISPLAY_NAME_MIN) {
    return `Display name must be at least ${DISPLAY_NAME_MIN} characters`;
  }
  if (trimmed.length > DISPLAY_NAME_MAX) {
    return `Display name must be at most ${DISPLAY_NAME_MAX} characters`;
  }
  if (!DISPLAY_NAME_PATTERN.test(trimmed)) {
    return "Display name can only contain letters, numbers, underscores, and hyphens";
  }
  return undefined;
}

/**
 * Validates a bio against backend rules.
 * Returns an error message or undefined if valid.
 */
export function validateBio(value: string): string | undefined {
  if (value.length > BIO_MAX) {
    return `Bio must be at most ${BIO_MAX} characters`;
  }
  return undefined;
}

/**
 * Validates an avatar file type and size.
 * Returns an error message or undefined if valid.
 */
export function validateAvatarFile(file: File): string | undefined {
  if (!AVATAR_ALLOWED_TYPES.includes(file.type)) {
    return "Avatar must be a JPEG, PNG, or WebP image";
  }
  if (file.size > AVATAR_MAX_SIZE) {
    return "Avatar must be 5 MB or smaller";
  }
  return undefined;
}

/**
 * Custom hook for managing the Edit Profile form state, validation,
 * and submission logic.
 *
 * @param userData - Current user data to pre-populate the form
 * @param onSuccess - Callback invoked on successful profile update with the updated user
 */
export function useEditProfileForm(
  userData: EditProfileUserData,
  onSuccess?: (updatedUser: any) => void,
  isOpen?: boolean
): UseEditProfileFormResult {
  const [displayName, setDisplayName] = useState<string>(userData.username);
  const [bio, setBio] = useState<string>(userData.bio || "");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(
    userData.avatarUrl || null
  );
  const [selectedAvatarId, setSelectedAvatarId] = useState<string | null>(null);
  const [errors, setErrors] = useState<EditProfileFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Reset form state when modal opens (isOpen transitions from false to true)
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      // Modal just opened — reset to current user data
      setDisplayName(userData.username);
      setBio(userData.bio || "");
      setAvatarFile(null);
      setAvatarPreview(userData.avatarUrl || null);
      setSelectedAvatarId(null);
      setErrors({});
    }
    prevOpenRef.current = !!isOpen;
  });

  const bioRemaining = BIO_MAX - bio.length;

  /**
   * Handle display name change with real-time validation clearing
   */
  const handleDisplayNameChange = useCallback((value: string) => {
    setDisplayName(value);
    // Clear display name error if the new value is valid
    if (errors.displayName) {
      const error = validateDisplayName(value);
      if (!error) {
        setErrors((prev) => {
          const { displayName: _, ...rest } = prev;
          return rest;
        });
      }
    }
  }, [errors.displayName]);

  /**
   * Handle bio change with real-time validation clearing
   */
  const handleBioChange = useCallback((value: string) => {
    setBio(value);
    // Clear bio error if the new value is valid
    if (errors.bio) {
      const error = validateBio(value);
      if (!error) {
        setErrors((prev) => {
          const { bio: _, ...rest } = prev;
          return rest;
        });
      }
    }
  }, [errors.bio]);

  /**
   * Handle avatar file selection with type/size validation
   */
  const handleAvatarSelect = useCallback((file: File) => {
    const error = validateAvatarFile(file);
    if (error) {
      setErrors((prev) => ({ ...prev, avatar: error }));
      return;
    }

    // Clear any previous avatar error
    setErrors((prev) => {
      const { avatar: _, ...rest } = prev;
      return rest;
    });

    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  }, []);

  /**
   * Handle preset avatar selection — update preview locally without API call.
   * The actual persist happens on form submit.
   */
  const handlePresetAvatarSelect = useCallback((avatarId: string, previewUrl: string) => {
    setSelectedAvatarId(avatarId);
    setAvatarPreview(previewUrl);
    setAvatarFile(null);
    setErrors((prev) => {
      const { avatar: _, ...rest } = prev;
      return rest;
    });
  }, []);

  /**
   * Validate all fields before submission.
   * Returns true if valid, false otherwise.
   */
  const validateAll = useCallback((): boolean => {
    const newErrors: EditProfileFormErrors = {};

    const displayNameError = validateDisplayName(displayName);
    if (displayNameError) {
      newErrors.displayName = displayNameError;
    }

    const bioError = validateBio(bio);
    if (bioError) {
      newErrors.bio = bioError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [displayName, bio]);

  /**
   * Submit the form: build FormData, call API with 30s timeout,
   * handle success/error responses.
   * Returns true on success, false on failure.
   */
  const handleSubmit = useCallback(async (): Promise<boolean> => {
    if (!validateAll()) {
      return false;
    }

    setIsSubmitting(true);
    setErrors({});

    const formData = new FormData();
    formData.append("username", displayName.trim());
    formData.append("bio", bio);

    if (avatarFile) {
      formData.append("avatar", avatarFile);
    }

    try {
      // If a preset avatar was selected, call selectAvatar first
      if (selectedAvatarId && selectedAvatarId !== '__linked__') {
        await api.app.selectAvatar(selectedAvatarId);
      } else if (selectedAvatarId === '__linked__') {
        // Linked account avatar — set profileImageUrl via selectAvatar flow
        // The linked avatar URL is already set as preview; just proceed with profile update
      }

      const response = await Promise.race([
        api.app.updateProfile(formData),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("timeout")), SUBMIT_TIMEOUT_MS)
        ),
      ]);

      setIsSubmitting(false);
      onSuccess?.(response.user);
      return true;
    } catch (err: unknown) {
      setIsSubmitting(false);

      if (err instanceof Error && err.message === "timeout") {
        setErrors({ general: "Request timed out. Please try again." });
        return false;
      }

      if (err instanceof ApiError && err.status === 400 && err.data?.errors) {
        // Map server validation errors to field-level errors
        const fieldErrors: EditProfileFormErrors = {};
        for (const entry of err.data.errors as Array<{ field: string; reason: string }>) {
          if (entry.field === "username") {
            fieldErrors.displayName = entry.reason;
          } else if (entry.field === "bio") {
            fieldErrors.bio = entry.reason;
          } else if (entry.field === "avatar") {
            fieldErrors.avatar = entry.reason;
          } else {
            fieldErrors.general = entry.reason;
          }
        }
        setErrors(fieldErrors);
        return false;
      }

      if (err instanceof ApiError) {
        setErrors({ general: err.message || "Something went wrong. Please try again." });
        return false;
      }

      setErrors({ general: "Something went wrong. Please try again." });
      return false;
    }
  }, [validateAll, displayName, bio, avatarFile, selectedAvatarId, onSuccess]);

  return {
    displayName,
    bio,
    avatarFile,
    avatarPreview,
    selectedAvatarId,
    errors,
    isSubmitting,
    bioRemaining,
    handleDisplayNameChange,
    handleBioChange,
    handleAvatarSelect,
    handlePresetAvatarSelect,
    handleSubmit,
  };
}
